import {
  captureVaultTabsSnapshotForExpiryLogout,
  clearVaultTabsSnapshotSession,
} from './vault-nav-restore.util';
import {
  normalizeVaultSuperadminEmail,
  readSessionUserEmail,
  vaultSessionIdentitiesMatch,
} from './vault-canonical-superadmin.util';

/** Ruta por defecto tras login cuando el usuario cerró sesión manualmente. */
export const VAULT_DEFAULT_POST_LOGIN_ROUTE = '/layout/vault/dashboard';

export const VAULT_POST_LOGIN_RETURN_URL_KEY = 'vault_post_login_return_url';
export const VAULT_LOGOUT_REASON_KEY = 'vault_logout_reason';
/** Email del usuario dueño de la sesión capturada para restaurar (#0000004). */
export const VAULT_EXPIRY_RESTORE_OWNER_KEY = 'vault_expiry_restore_owner';

export type VaultLogoutReason = 'expiry' | 'manual';

export function isVaultLayoutPath(url: string): boolean {
  const path = (url ?? '').split('?')[0].split('#')[0];
  return path.includes('/layout/vault');
}

export function markVaultLogoutReason(reason: VaultLogoutReason): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(VAULT_LOGOUT_REASON_KEY, reason);
}

export function readVaultLogoutReason(): VaultLogoutReason | null {
  if (typeof window === 'undefined') return null;
  const value = sessionStorage.getItem(VAULT_LOGOUT_REASON_KEY);
  return value === 'expiry' || value === 'manual' ? value : null;
}

/** Guarda la ruta Vault actual para restaurarla tras expiración por inactividad. */
export function captureVaultReturnUrlForExpiryLogout(): void {
  if (typeof window === 'undefined') return;
  const current = `${window.location.pathname}${window.location.search}`;
  if (!isVaultLayoutPath(current)) return;
  sessionStorage.setItem(VAULT_POST_LOGIN_RETURN_URL_KEY, current);
}

/** Guarda el email del usuario dueño de la sesión (una vez por ciclo de expiración). */
function captureVaultExpiryRestoreOwner(): void {
  if (typeof window === 'undefined') return;
  if (sessionStorage.getItem(VAULT_EXPIRY_RESTORE_OWNER_KEY)) return;
  const email = normalizeVaultSuperadminEmail(readSessionUserEmail());
  if (email) {
    sessionStorage.setItem(VAULT_EXPIRY_RESTORE_OWNER_KEY, email);
  }
}

export function readVaultExpiryRestoreOwner(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(VAULT_EXPIRY_RESTORE_OWNER_KEY)?.trim() || null;
}

/**
 * ¿La sesión capturada pertenece al usuario indicado?
 * Sin dueño registrado → no restaurar (evita que otro rol herede pestañas/Bóveda del admin).
 * Con dueño distinto → no restaurar.
 * Equivalencia QA: `qa@datavault` ≈ `qa@datavault.com` (#0000161 / #0000004).
 */
export function vaultExpiryRestoreBelongsToUser(email: string | null | undefined): boolean {
  const owner = readVaultExpiryRestoreOwner();
  if (!owner) return false;
  return vaultSessionIdentitiesMatch(owner, email);
}

/** Limpia todo el estado de restauración por expiración (ruta, motivo, pestañas, dueño). */
export function clearVaultExpiryRestoreSession(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(VAULT_POST_LOGIN_RETURN_URL_KEY);
  sessionStorage.removeItem(VAULT_LOGOUT_REASON_KEY);
  sessionStorage.removeItem(VAULT_EXPIRY_RESTORE_OWNER_KEY);
  clearVaultTabsSnapshotSession();
}

/** Ruta + pestañas abiertas antes de cierre automático (#0000004). */
export function captureVaultSessionForExpiryLogout(): void {
  captureVaultExpiryRestoreOwner();
  captureVaultReturnUrlForExpiryLogout();
  captureVaultTabsSnapshotForExpiryLogout();
  if (readVaultLogoutReason() !== 'expiry') {
    markVaultLogoutReason('expiry');
  }
}

/** Tras cerrar sesión manualmente, volver a Inicio al reingresar. */
export function setVaultPostLoginRouteToDashboard(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(VAULT_POST_LOGIN_RETURN_URL_KEY, VAULT_DEFAULT_POST_LOGIN_ROUTE);
  markVaultLogoutReason('manual');
}

export function readVaultPostLoginReturnUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const url = sessionStorage.getItem(VAULT_POST_LOGIN_RETURN_URL_KEY)?.trim();
  if (!url || !isVaultLayoutPath(url)) return null;
  return url;
}

/** Lee y elimina la ruta pendiente (una sola vez por login). */
export function consumeVaultPostLoginReturnUrl(): string | null {
  const url = readVaultPostLoginReturnUrl();
  if (typeof window === 'undefined') return url;
  sessionStorage.removeItem(VAULT_POST_LOGIN_RETURN_URL_KEY);
  sessionStorage.removeItem(VAULT_LOGOUT_REASON_KEY);
  sessionStorage.removeItem(VAULT_EXPIRY_RESTORE_OWNER_KEY);
  return url;
}
